<template>
  <calendar-view
    :show-date="showDate"
    class="theme-default"
    displayPeriodUom="year"
    :displayPeriodCount="2"
    locale="ja-JP"
    month-name-format="numeric"
    :items="calendarItems"
    itemContentHeight="40px"
    itemBorderHeight="4px"
    @click-item="openActiveDialog"
  >
    <template #header="{ headerProps }">
      <calendar-view-header :header-props="headerProps" @input="setShowDate" />
    </template>

    <template #dayContent="{ day }">
      <span
        class="text-orange font-bold text-lg text-center w-full"
        style="height: fit-content;"
        >{{ day.getMonth() + 1 + ' / ' + day.getDate() }}</span
      >
    </template>
  </calendar-view>
</template>

<script>
import { reactive, ref, onMounted, watch } from 'vue'
import { CalendarView, CalendarViewHeader } from 'vue-simple-calendar'

import 'vue-simple-calendar/dist/style.css'
// The next two lines are optional themes
import 'vue-simple-calendar/static/css/default.css'
import 'vue-simple-calendar/static/css/holidays-us.css'
import { useYearSelector } from '@/services/useYearSelector'
import { useMonthSelector } from '@/services/useMonthSelector'
import { useLogs } from '@/services/useLogs'
import { useBudgetDialog } from '@/services/useBudgetDialog'
import { useReceiptDialog } from '@/services/receiptsOsaka/useReceiptDialog'
import { usePoliticalActionDialog } from '@/services/politicalAction/usePoliticalActionDialog'
import { useDialog } from '@/services/useDialog'
import { expenseTypesToString } from '@/services/useExpenseType'

export default {
  name: 'AppCalendar_OSAKA',

  components: {
    CalendarView,
    CalendarViewHeader,
  },

  setup() {
    const { currentMonth } = useMonthSelector()
    const { currentYear } = useYearSelector()
    const { getTimelineTop, timelineTop } = useLogs()
    const { openBudgetPreview } = useBudgetDialog()
    const { openReceiptDialog } = useReceiptDialog()
    const { changeDocument } = usePoliticalActionDialog()
    const { isOpen, type } = useDialog()
    const calendarItems = reactive([])
    const showDate = ref(
      new Date(currentYear.value, new Date().getMonth(), new Date().getDate()),
    )

    const openActiveDialog = e => {
      if (e.originalItem.documentType === 1) {
        openBudgetPreview(e.id)
        return
      } else if (e.originalItem.documentType === 2) {
        openReceiptDialog(e.id)
        return
      } else {
        changeDocument(3, e.id)
        return
      }
    }

    const expenseType = type => {
      let res = expenseTypesToString(type)
      return res !== '' ? res : '未設定'
    }

    const contentType = type => {
      switch (type) {
        case 0:
          return '交付金'
        case 1:
          return '前期交付金'
        case 2:
          return '後期交付金'
        case 3:
          return '一部変返還'
        case 4:
          return '決算利息'
        default:
          return '未設定'
      }
    }

    const actionType = type => {
      switch (type) {
        case 1:
          return '調査研究'
        case 2:
          return '研修'
        case 3:
          return '調査研究/研修'
        case 4:
          return '要望・陳情'
        case 5:
          return '調査研究/要望・陳情'
        case 6:
          return '研修/要望・陳情'
        case 7:
          return '調査研究/研修/要望・陳情'
        default:
          return '未設定'
      }
    }

    onMounted(async () => {
      await getTimelineTop()
      // openActiveDialog
      timelineTop.value.content.forEach(item => {
        if (item.documentType == 1) {
          const budget = {
            documentType: (item.documentType = 1),
            id: item.budget.id,
            startDate: item.budget.paymentDate,
            title: contentType(item.budget.contentId),
            classes: ['budget-calendar-item'],
          }
          calendarItems.push(budget)
        }
        if (item.documentType == 2) {
          const receipt = {
            documentType: (item.documentType = 2),
            id: item.receipt.id,
            startDate: item.receipt.expenseDate,
            title: expenseType(item.receipt.expenseTypeId),
            classes: ['receipt-calendar-item'],
          }
          calendarItems.push(receipt)
        }
        if (item.documentType == 3) {
          const politicalAction = {
            documentType: (item.documentType = 3),
            id: item.politicalAction.id,
            startDate: item.politicalAction.startDate,
            endDate: item.politicalAction.endDate,
            title: actionType(item.politicalAction.actionTypes),
            classes: ['activity-calendar-item'],
          }
          calendarItems.push(politicalAction)
        }
      })

      // finding today's weekday and adding class
      let calendarHeaders = document.getElementsByClassName('cv-header-day')
      const today = new Date().getDay()
      let todayCell = calendarHeaders[today]
      todayCell.classList.add('today-weekday')

      // auto scroll to today
      const todayTableCell = document.getElementsByClassName('today')[0]
      if (todayTableCell) {
        todayTableCell.scrollIntoView()
      }

      // add hr after week row
      const startCalendar = document.getElementsByClassName('d04-01')[0]
        .parentElement.parentElement

      const endCalendar = document.getElementsByClassName(
        `d${currentYear.value + 1}-03-31`,
      )[0].parentElement.parentElement

      let weekRows = document.getElementsByClassName('cv-week')
      for (let weekRow of weekRows) {
        const classOfWeekRow = weekRow.classList[weekRow.classList.length - 1]
        if (
          classOfWeekRow <
          startCalendar.classList[startCalendar.classList.length - 1]
        ) {
          weekRow.style.display = 'none'
        }

        if (
          classOfWeekRow >
          endCalendar.classList[startCalendar.classList.length - 1]
        ) {
          weekRow.style.display = 'none'
        }

        if (
          classOfWeekRow >=
            startCalendar.classList[startCalendar.classList.length - 1] &&
          classOfWeekRow <=
            endCalendar.classList[startCalendar.classList.length - 1]
        ) {
          weekRow.insertAdjacentHTML(
            'afterend',
            "<hr class='border-1 border-orange' />",
          )
        }
      }

      const weekdayHeaders = document.getElementsByClassName(
        'cv-header-days',
      )[0]
      weekdayHeaders.insertAdjacentHTML(
        'afterend',
        "<hr class='border-2 border-orange' />",
      )
    })

    const setShowDate = d => {
      showDate.value = d
    }

    watch(currentMonth, currentMonth => {
      const monthElement = document.getElementsByClassName(
        'd' + ('0' + `${currentMonth}`).slice(-2) + '-01',
      )
      if (currentMonth >= 4) {
        monthElement[0].scrollIntoView()
      } else {
        monthElement[monthElement.length - 1].scrollIntoView()
      }
    })

    return {
      showDate,
      setShowDate,
      currentYear,
      timelineTop,
      calendarItems,
      isOpen,
      type,
      openActiveDialog,
      expenseType,
      contentType,
      actionType,
      CalendarView,
    }
  },
}
</script>
<style lang="scss">
.cv-header {
  display: none;
}
.cv-week {
  min-height: 150px !important;
  margin-top: 9px;
  margin-bottom: 9px;
}

.cv-weeks {
  // max-height: 500px;
  max-height: calc(100vh - 260px);
}

.cv-day,
.cv-header-day,
.cv-header-days,
.cv-item,
.cv-week,
.cv-weeks {
  border: none !important;
}

//月/日の親要素
.theme-default .cv-day.outsideOfMonth {
  span {
    background-color: theme('colors.orange.lightest') !important;
    // position: absolute !important;
    // z-index: 200 !important;
  }
  background-color: rgb(255, 255, 255) !important;
  // position: relative !important;
  z-index: -1 !important;
  // overflow: hidden;
}

//不明
.theme-default .cv-header {
  background: #fff !important;
}

//月火水木金土日
.cv-header-day {
  font-family: Noto Sans JP !important;
  font-style: normal !important;
  font-weight: bold !important;
  font-size: 18px !important;
  line-height: 26px !important;
  color: theme('colors.orange.DEFAULT');
  background-color: rgb(255, 255, 255) !important;
}

.cv-day-number {
  margin-left: auto;
  margin-right: auto;
  display: none;
}

//本日の曜日につく○
.today-weekday {
  position: sticky;
  &::after {
    content: ' ';
    width: 28px;
    height: 28px;
    border: 1px solid theme('colors.orange.DEFAULT');
    position: absolute;
    border-radius: 28px;
    // z-index: -1;
  }
}

.cv-day.outsideOfMonth.past.last.lastInstance {
  span {
    border-top-right-radius: 999px;
    border-bottom-right-radius: 999px;
  }
}

.cv-day.outsideOfMonth.past {
  &.d01 {
    span {
      border-top-left-radius: 999px;
      border-bottom-left-radius: 999px;
    }
  }
}

.cv-day.outsideOfMonth.future {
  &.d01 {
    span {
      border-top-left-radius: 999px;
      border-bottom-left-radius: 999px;
    }
  }
}

.cv-day.outsideOfMonth.future.last.lastInstance {
  span {
    border-top-right-radius: 999px;
    border-bottom-right-radius: 999px;
  }
}

.cv-day {
  border: 1px solid theme('colors.orange.DEFAULT') !important;
  border-bottom: none !important;
  box-sizing: border-box;
  margin-top: -1px;
}
.cv-day:first-child {
  border-left: 1px solid white !important;
}

.cv-day:not(:first-child) {
  margin-top: -1px;
  margin-left: -1px;
  border-right: 1px solid white !important;
}

.cv-header-days {
  margin-bottom: 10px;
  margin-top: 10px;
}

.cv-header-day {
  border-right: 1px solid theme('colors.orange.DEFAULT') !important;
}
.cv-header-day:first-child {
  border-left: 1px solid white !important;
}
.cv-header-day:last-child {
  border-right: 1px solid white !important;
}

.activity-calendar-item {
  height: 40px !important;
  background: #ffffff !important;
  border: 4px solid theme('colors.blue.DEFAULT') !important;
  box-sizing: border-box !important;
  border-radius: 24px !important;
  font-family: Noto Sans JP !important;
  font-style: normal !important;
  font-weight: normal !important;
  font-size: 18px !important;
  // line-height: 20px !important;
  color: #000000 !important;
  text-align: left !important;
  margin-top: 12px;
  padding-left: 16px !important;
  padding-right: 16px !important;
  z-index: 50 !important;
  &::before {
    content: '';
    width: 20px;
    height: 20px;
    display: inline-flex;
    margin-right: 4px;
    background-color: theme('colors.blue.DEFAULT');
    -webkit-mask: url('../../../assets/icons/lightning-red.svg') no-repeat
      center;
    mask: url('../../../assets/icons/lightning-red.svg') no-repeat center;
    top: 3px;
    position: sticky;
  }
}

.budget-calendar-item {
  height: 40px !important;
  background: #ffffff !important;
  border: 4px solid theme('colors.yellow.DEFAULT') !important;
  box-sizing: border-box !important;
  border-radius: 24px !important;
  font-family: Noto Sans JP !important;
  font-style: normal !important;
  font-weight: normal !important;
  font-size: 18px !important;
  // line-height: 20px !important;
  color: #000000 !important;
  text-align: left !important;
  margin-top: 12px;
  padding-left: 16px !important;
  padding-right: 16px !important;

  &::before {
    content: '';
    width: 20px;
    height: 20px;
    display: inline-flex;
    margin-right: 4px;
    background-color: theme('colors.yellow.DEFAULT');
    -webkit-mask: url('../../../assets/icons/money-circle-red.svg') no-repeat
      center;
    mask: url('../../../assets/icons/money-circle-red.svg') no-repeat center;
    top: 3px;
    position: sticky;
  }
}

.receipt-calendar-item {
  height: 40px !important;
  background: #ffffff !important;
  border: 4px solid theme('colors.green.DEFAULT') !important;
  box-sizing: border-box !important;
  border-radius: 24px !important;
  font-family: Noto Sans JP !important;
  font-style: normal !important;
  font-weight: normal !important;
  font-size: 18px !important;
  // line-height: 20px !important;
  color: #000000 !important;
  text-align: left !important;
  margin-top: 12px;
  padding-left: 16px !important;
  padding-right: 16px !important;

  &::before {
    content: '';
    width: 20px;
    height: 20px;
    display: inline-flex;
    margin-right: 4px;
    background-color: theme('colors.green.DEFAULT');
    -webkit-mask: url('../../../assets/icons/receiptred.svg') no-repeat center;
    mask: url('../../../assets/icons/receiptred.svg') no-repeat center;
    top: 3px;
    position: sticky;
  }
}
.cv-item.continued {
  line-height: 22px !important;
  border-left: none !important;
  border-top-left-radius: 0px !important;
  border-bottom-left-radius: 0px !important;
}

.cv-item.toBeContinued {
  border-right: none !important;
  border-top-right-radius: 0px !important;
  border-bottom-right-radius: 0px !important;
}

.today {
  span {
    color: theme('colors.orange.DEFAULT');
    &::after {
      content: ' ';
      width: 80px;
      height: 32px;
      background: theme('colors.orange.lightest');
      border-radius: 16px;
      // z-index: -1;
      position: absolute;
      left: 0;
      right: 0;
      margin-left: auto;
      margin-right: auto;
    }
  }
}
.theme-default .cv-day.today {
  background: #fff !important;
}
</style>
